// Page metadata → document head tags.
//
// Pages describe their metadata with `Metadata` (the subset of fields the site uses) and turn it
// into a TanStack Router `head` with `metadataHead()`:
//
//   export const Route = createFileRoute("/terms")({
//     head: () => metadataHead({ title: "Terms of Service", alternates: { canonical: "/terms" } }),
//   });
//
// The root route renders ROOT_METADATA (src/lib/Seo.ts). Child routes only list what they change;
// TanStack's <HeadContent> keeps the last `title` and the deepest meta tag per name/property, so a
// child's description or Open Graph title replaces the root's. Titles get the site template
// (`%s | CalmLens`) unless they are `{ absolute }`. Relative URLs resolve against SITE_URL.
import { TITLE } from "shared/SharedConstants";

export type MetadataTitle = string | { default?: string; template?: string; absolute?: string };

export interface MetadataImage {
  url: string;
  width?: number;
  height?: number;
  alt?: string;
  type?: string;
}

export interface MetadataRobots {
  index?: boolean;
  follow?: boolean;
  googleBot?: {
    index?: boolean;
    follow?: boolean;
    "max-image-preview"?: "none" | "standard" | "large";
    "max-snippet"?: number;
    "max-video-preview"?: number;
  };
}

export interface Metadata {
  metadataBase?: URL;
  title?: MetadataTitle;
  description?: string;
  applicationName?: string;
  keywords?: string[];
  authors?: { name: string; url?: string | URL }[];
  creator?: string;
  publisher?: string;
  category?: string;
  referrer?: string;
  icons?: { icon: { url: string; type?: string; sizes?: string } };
  manifest?: string;
  /** `markdown` is the same page as Markdown, linked for tools that read pages without a browser. */
  alternates?: { canonical?: string; markdown?: string };
  robots?: MetadataRobots;
  openGraph?: {
    type?: "website" | "article";
    locale?: string;
    url?: string;
    siteName?: string;
    title?: string;
    description?: string;
    publishedTime?: string;
    modifiedTime?: string;
    authors?: string[];
    images?: MetadataImage[];
  };
  twitter?: {
    card?: "summary" | "summary_large_image";
    title?: string;
    description?: string;
    images?: MetadataImage[];
  };
}

export type HeadMeta =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string }
  | { charSet: string };

export interface HeadLink {
  rel: string;
  href: string;
  type?: string;
  sizes?: string;
  as?: string;
  crossOrigin?: "anonymous" | "use-credentials";
}

export interface Head {
  meta: HeadMeta[];
  links: HeadLink[];
}

export const DEFAULT_SITE_URL = new URL("https://calmlens.com");
export const TITLE_TEMPLATE = `%s | ${TITLE}`;

/** The document title a route's metadata produces (template applied unless absolute). */
export const resolveTitle = (
  title: MetadataTitle | undefined,
  template = TITLE_TEMPLATE,
): string | undefined => {
  if (title === undefined) return undefined;
  if (typeof title === "string") return template.replace("%s", title);
  if (title.absolute) return title.absolute;
  return title.default;
};

const robotsContent = (robots: Omit<MetadataRobots, "googleBot">) => [
  robots.index === false ? "noindex" : "index",
  robots.follow === false ? "nofollow" : "follow",
];

const googleBotContent = (googleBot: NonNullable<MetadataRobots["googleBot"]>) =>
  [
    ...robotsContent(googleBot),
    ...(googleBot["max-video-preview"] !== undefined
      ? [`max-video-preview:${googleBot["max-video-preview"]}`]
      : []),
    ...(googleBot["max-image-preview"]
      ? [`max-image-preview:${googleBot["max-image-preview"]}`]
      : []),
    ...(googleBot["max-snippet"] !== undefined ? [`max-snippet:${googleBot["max-snippet"]}`] : []),
  ].join(", ");

const imageTags = (prefix: "og:image" | "twitter:image", images: MetadataImage[], base: URL) =>
  images.flatMap((image): HeadMeta[] => {
    const key = prefix === "og:image" ? "property" : "name";
    const tag = (suffix: string, content: string | number | undefined): HeadMeta[] =>
      content === undefined
        ? []
        : [{ [key]: `${prefix}${suffix}`, content: String(content) } as HeadMeta];
    return [
      ...tag("", new URL(image.url, base).toString()),
      ...tag(":width", image.width),
      ...tag(":height", image.height),
      ...tag(":type", image.type),
      ...tag(":alt", image.alt),
    ];
  });

/** Head tags for `metadata`. Pass `{ template: false }` for the root, whose title is the default. */
export const metadataHead = (
  metadata: Metadata,
  options: { siteUrl?: URL; template?: string | false } = {},
): Head => {
  const base = metadata.metadataBase ?? options.siteUrl ?? DEFAULT_SITE_URL;
  const absolute = (url: string | URL) => new URL(url, base).toString();
  const meta: HeadMeta[] = [];
  const links: HeadLink[] = [];
  const named = (name: string, content: string | undefined) => {
    if (content !== undefined) meta.push({ name, content });
  };
  const property = (name: string, content: string | undefined) => {
    if (content !== undefined) meta.push({ property: name, content });
  };

  const title =
    options.template === false
      ? resolveTitle(metadata.title, "%s")
      : resolveTitle(metadata.title, options.template);
  if (title !== undefined) meta.push({ title });
  named("description", metadata.description);
  named("application-name", metadata.applicationName);
  for (const author of metadata.authors ?? []) {
    named("author", author.name);
    if (author.url) links.push({ rel: "author", href: absolute(author.url) });
  }
  if (metadata.keywords?.length) named("keywords", metadata.keywords.join(","));
  named("creator", metadata.creator);
  named("publisher", metadata.publisher);
  named("category", metadata.category);
  named("referrer", metadata.referrer);
  if (metadata.robots) {
    named("robots", robotsContent(metadata.robots).join(", "));
    if (metadata.robots.googleBot) named("googlebot", googleBotContent(metadata.robots.googleBot));
  }
  if (metadata.alternates?.canonical) {
    links.push({ rel: "canonical", href: absolute(metadata.alternates.canonical) });
  }
  if (metadata.alternates?.markdown) {
    links.push({
      rel: "alternate",
      type: "text/markdown",
      href: absolute(metadata.alternates.markdown),
    });
  }
  if (metadata.icons) {
    links.push({
      rel: "icon",
      href: metadata.icons.icon.url,
      type: metadata.icons.icon.type,
      sizes: metadata.icons.icon.sizes,
    });
  }
  if (metadata.manifest) links.push({ rel: "manifest", href: metadata.manifest });

  const openGraph = metadata.openGraph;
  if (openGraph) {
    property("og:title", openGraph.title);
    property("og:description", openGraph.description);
    property("og:url", openGraph.url === undefined ? undefined : absolute(openGraph.url));
    property("og:site_name", openGraph.siteName);
    property("og:locale", openGraph.locale);
    meta.push(...imageTags("og:image", openGraph.images ?? [], base));
    property("og:type", openGraph.type);
    property("article:published_time", openGraph.publishedTime);
    property("article:modified_time", openGraph.modifiedTime);
    for (const author of openGraph.authors ?? []) property("article:author", author);
  }
  const twitter = metadata.twitter;
  if (twitter) {
    named("twitter:card", twitter.card);
    named("twitter:title", twitter.title);
    named("twitter:description", twitter.description);
    meta.push(...imageTags("twitter:image", twitter.images ?? [], base));
  }
  return { meta, links };
};
